/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React from 'react';
import classNames from 'classnames';
import { Text } from '../../../atoms/Text/Text';
import './SourceCitationRendererStyles.scss';
const baseClass = 'qmb-ui-SourceCitationRenderer';
/**
 * Only `http:`/`https:` article URLs are rendered as links. Anything else — most
 * importantly `javascript:` — is rendered as plain text so an untrusted citation
 * URL cannot execute when the title is activated. Same guard as `UpsellRenderer`.
 */
function isSafeUrl(url) {
    if (typeof url !== 'string')
        return false;
    return /^https?:\/\//i.test(url);
}
function SourceRow({ entry }) {
    const safe = isSafeUrl(entry.article_url);
    const content = (React.createElement(React.Fragment, null,
        React.createElement("i", { className: classNames('fa-light fa-book-open', `${baseClass}__icon`), "aria-hidden": "true" }),
        React.createElement("span", { className: `${baseClass}__title` }, entry.article_title),
        safe ? (React.createElement("i", { className: classNames('fa-light fa-arrow-up-right-from-square', `${baseClass}__external`), "aria-hidden": "true" })) : null));
    if (!safe) {
        return (React.createElement("li", { className: `${baseClass}__item` },
            React.createElement("span", { className: `${baseClass}__row ${baseClass}__row--inert` }, content)));
    }
    return (React.createElement("li", { className: `${baseClass}__item` },
        React.createElement("a", { className: `${baseClass}__row`, href: entry.article_url, target: "_blank", rel: "noopener noreferrer" }, content)));
}
/**
 * SourceCitationRenderer
 *
 * Compact Help Center provenance under a Support Agent answer: eyebrow + one
 * outbound link row per article. Not an AIGeneralBlockCard — that chrome (empty
 * section body + copy button) read as a half-built form, not a source cite.
 *
 * Sibling of — deliberately not an extension of — `CitationRenderer`, which is the
 * NFPA compliance-citation shape (`standard`/`edition_year`/`section`) with a
 * required `shall`/`should`/`may` obligation badge. Support articles have no
 * obligation classification, so there is no honest value to put in that field.
 *
 * `similarity_score` is an internal retrieval diagnostic and is intentionally never
 * rendered. `snippet` is kept on the wire (pinned contract) but ignored here.
 *
 * @version 1.2.0
 * @since 2026-07-30
 */
export function SourceCitationRenderer({ block }) {
    var _a;
    const sourceCitation = block;
    const citations = (_a = sourceCitation.citations) !== null && _a !== void 0 ? _a : [];
    if (citations.length === 0) {
        return null;
    }
    return (React.createElement("aside", { className: baseClass, "aria-label": "Help Center sources" },
        React.createElement(Text, { variant: "xx-small", as: "p", className: `${baseClass}__eyebrow` }, "From Help Center"),
        React.createElement("ul", { className: `${baseClass}__list` }, citations.map((entry) => (React.createElement(SourceRow, { key: `${entry.article_url || 'unsafe'}:${entry.article_title}`, entry: entry }))))));
}
